// useVibeQuery — Vibe DB reads with realtime refresh. Wraps TanStack useQuery under ['vibedb', ...].
// The socket signals WHICH collections changed (never carries data); a global listener refetches
// queries whose declared `collections` intersect (stashed in query meta, invalidated by predicate).
import { useEffect } from 'react';
import { type Query, QueryClient, QueryClientProvider, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getVibeSocket } from './vibe-socket';

// One module-level client for the SPA. staleTime: the socket is the freshness signal, so remounts
// within 30s serve cache instead of re-hitting the Lambda → DO path.
export const vibeQueryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
});

// Refetch only MOUNTED Vibe queries matching `predicate` (refetchType:'active'); unmounted queries go
// stale and refetch on next mount. A failed refetch is swallowed — it just leaves the stale data.
function refetchActiveVibeQueries(predicate: (query: Query) => boolean): void {
  vibeQueryClient.invalidateQueries({ predicate, refetchType: 'active' }).catch(() => {});
}

// Socket → cache bridge, registered once (opening the socket stays lazy): a write invalidates the
// queries whose declared collections it touched.
getVibeSocket().onInvalidate((changed) => {
  refetchActiveVibeQueries((query) => {
    const cols = query.meta?.vibeCollections as string[] | undefined;
    return Array.isArray(cols) && cols.some((c) => changed.has(c));
  });
});

// After any reconnect, writes may have landed while we were offline (their invalidations were never
// delivered), so refetch every active Vibe query once to reconcile. Only fires on reconnects beyond
// the first connection, so the initial mount isn't double-fetched.
getVibeSocket().onReconnect(() => {
  refetchActiveVibeQueries((query) => Array.isArray(query.meta?.vibeCollections));
});

export interface UseVibeQueryOptions {
  /** Collections this query reads. Any invalidation for one of these triggers a refetch. */
  collections: string[];
  /** Set false to defer the query (e.g. dependent on a selected id); mirrors useQuery's `enabled`. */
  enabled?: boolean;
}

/**
 * @param queryKey  Stable key for this read (e.g. `['tasks']` or `['task', id]`). Namespaced under
 *                  `['vibedb', ...]` internally.
 * @param serverFn  A server function that reads Vibe DB and returns the data (the only data path).
 * @param options   `{ collections }` — the collections to refetch on when they change.
 */
export function useVibeQuery<T>(
  queryKey: ReadonlyArray<unknown>,
  serverFn: () => Promise<T>,
  { collections, enabled }: UseVibeQueryOptions,
): UseQueryResult<T> {
  useEffect(() => {
    getVibeSocket().connect();
  }, []);

  return useQuery<T>({
    queryKey: ['vibedb', ...queryKey],
    queryFn: () => serverFn(),
    meta: { vibeCollections: collections },
    enabled,
  });
}

/** Provider for the shared Vibe DB query client. Wrap the app once (done in __root). */
export { QueryClientProvider };

export default useVibeQuery;
